import React, { useState, useEffect } from "react";
import { Title, Tab, Tag } from "rbx";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import { Switch, useParams, useLocation, Route, Link } from "react-router-dom";
import {
  getTagColorByOrderStatus,
  calcTimeZone,
  getDifferenceByTimeZone
} from "../../../utils";
import { SINGLE_ORDER_ARCHIVE_QUERY } from "../../../graphql/OrderArchives/OrderArchives.graphql";

import IconLink from "../../../components/IconLink";

import Summary from "../Orders/routes/Summary";
import Billing from "../Orders/routes/Billing";
import BolEld from "../Orders/routes/BolEld";

import Authorization, {
  useAuthorization
} from "../../../components/Authorization";

const BillingAuth = Authorization(["ADMIN", "FIN"]);

const SIMPLE_ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

function pad(number) {
  if (number < 10) {
    return `0${number}`;
  }
  return number;
}
// eslint-disable-next-line no-extend-native
Date.prototype.toCorrectDateString = function() {
  return `${this.getFullYear()}-${pad(this.getMonth() + 1)}-${pad(
    this.getDate()
  )}T${pad(this.getHours())}:${pad(this.getMinutes())}`;
};
const INITIAL_STATE = {
  RecId: "",
  OrderNumber: "",
  StatusRecIdRef: "",
  CustomerRecIdRef: "",
  RouteRecIdRef: "",
  LoadTypeRecIdRef: "",
  StartLocationRecIdRef: "",
  PickupLocationRecIdRef: "",
  DeliveryLocationRecIdRef: "",
  EndingLocationRecIdRef: "",
  Customer_SI: "",
  StartLoc_SI: "",
  PickupLoc_SI: "",
  DeliveryLoc_SI: "",
  EndLoc_SI: "",
  EstimatedLoad: "",
  EstimatedTravel: "",
  EstimatedUload: "",
  OrderDate: "",
  RequestedDeliveryDate: "",
  ScheduledDeliveryDate: "",
  PickupDate: "",
  BillToRecIdRef: null,
  PaymentMethodRecIdRef: "",
  CustRef: "",
  VoidDate: "",
  VoidReason: "",
  VoidNotificationContact: "",
  VoidNotificationDate: "",
  VoidNotificationBy: "",
  VoidComments: "",
  CompanyId: "",
  GrossWeight: "",
  TareWeight: "",
  NetWeight: "",
  PickupDriverRecIdRef: "",
  PickupTractorRecIdRef: "",
  PickupTrailerRecIdRef: "",
  DeliveryDriverRecIdRef: "",
  DeliveryTractorRecIdRef: "",
  DeliveryTrailerRecIdRef: "",
  PickupTimeIn: "",
  PickupTimeOut: "",
  DeliveryTimeIn: "",
  DeliveryTimeOut: "",
  HotFlag: false,
  DateExported: "",
  DateImported: "",
  BTU: "",
  Scale: "",
  LoadNumber: "",
  TankID: "",
  TryCock: "",
  PickupInches: "",
  PickupPressure: "",
  PickupTemperature: "",
  DeliveryInches: "",
  DeliveryPressure: "",
  DeliveryTemperature: "",
  BookingCodeRecIdRef: "",
  LoadedMiles: "",
  TariffRecIdRef: "",
  ChargeForSaturday: false,
  ChargeForSunday: false,
  ChargeForHoliday: false,
  BillingNotes: "",
  OrderNotes: "",
  NonBill: false,
  Tolls: "",
  ChargeForTolls: "",
  Cancel: false,
  PaymentTermsRecIdRef: ""
};

const OrderArchivePage = () => {
  const SystemCodes = useQuery(SIMPLE_ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["EXA", "NB"]
      }
    }
  });
  const { id } = useParams();
  const location = useLocation();
  const isAuthorized = useAuthorization(["ADMIN", "FIN"]);
  const [States, setStates] = useState(null);

  const { data } = useQuery(SINGLE_ORDER_ARCHIVE_QUERY, {
    variables: { where: { RecId: id ? parseInt(id) : 0 } }
  });
  const [inputs, setInputs] = useState(INITIAL_STATE);
  const [orderDetails, setOrderDetails] = useState([]);

  // Lock orders that have been exported to accounting
  useEffect(() => {
    if (data && SystemCodes?.data?.SystemCodes?.length && !States) {
      const exportedToAccounting = SystemCodes.data.SystemCodes.find(
        s => s.CodeId === "EXA"
      );
      setStates({
        ExportedToAccounting: exportedToAccounting,
        NonBillable: SystemCodes.data.SystemCodes.find(s => s.CodeId === "NB")
      });
    }
  }, [States, SystemCodes, SystemCodes.data, data]);
  const handleChangeCustomer = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = { ...INITIAL_STATE };
    updates[e.target.name] = value ? parseInt(value) : null;
    setInputs(updates);
  };

  const handleChangeRoute = routeData => {
    const updates = { ...inputs, ...routeData };
    setInputs(updates);
    // setStoredValue(updates);
  };

  useEffect(() => {
    if (data && data.OrderArchive) {
      const updates = { ...data.OrderArchive };
      updates.OrderDate = new Date(updates.OrderDate).toCorrectDateString();
      updates.PickupDate = new Date(updates.PickupDate).toCorrectDateString();
      updates.RequestedDeliveryDate = new Date(
        updates.RequestedDeliveryDate
      ).toCorrectDateString();
      updates.ScheduledDeliveryDate = new Date(
        updates.ScheduledDeliveryDate
      ).toCorrectDateString();
      updates.PickupDateFormatted = calcTimeZone(
        updates.PickupDate,
        updates.PickupLocation.TimeZone.CodeId
      );
      updates.RequestedDeliveryDateFormatted = calcTimeZone(
        updates.RequestedDeliveryDate,
        updates.DeliveryLocation.TimeZone.CodeId
      );
      updates.ScheduledDeliveryDateFormatted = calcTimeZone(
        updates.ScheduledDeliveryDate,
        updates.DeliveryLocation.TimeZone.CodeId
      );
      updates.PickupTimeInFormatted = calcTimeZone(
        updates.PickupTimeIn,
        updates.PickupLocation.TimeZone.CodeId
      );
      updates.PickupTimeOutFormatted = calcTimeZone(
        updates.PickupTimeOut,
        updates.PickupLocation.TimeZone.CodeId
      );
      updates.DeliveryTimeInFormatted = calcTimeZone(
        updates.DeliveryTimeIn,
        updates.DeliveryLocation.TimeZone.CodeId
      );
      updates.DeliveryTimeOutFormatted = calcTimeZone(
        updates.DeliveryTimeOut,
        updates.DeliveryLocation.TimeZone.CodeId
      );
      updates.OrderedBy = !!(updates.CreatedBy && updates.OrderDateTime);
      setInputs(updates);
    }
  }, [data]);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    const updates = {
      [e.target.name]: value
    };

    if (e.target.name === "BillToRecIdRef") {
      updates.NonBill = parseInt(value) === 179;
    }

    if (e.target.name === "TareWeight") {
      updates.TareWeight = parseFloat(e.target.value);
      updates.NetWeight = inputs.GrossWeight - updates.TareWeight;
    }

    if (e.target.name === "GrossWeight") {
      updates.GrossWeight = parseFloat(e.target.value);
      updates.NetWeight = updates.GrossWeight - inputs.TareWeight;
    }

    if (e.target.name === "PickupLocation") {
      updates.PickupDateFormatted = calcTimeZone(
        inputs.PickupDate,
        e.target.value.TimeZone.CodeId
      );
    }
    if (e.target.name === "DeliveryLocation") {
      updates.RequestedDeliveryDateFormatted = calcTimeZone(
        inputs.RequestedDeliveryDate,
        e.target.value.TimeZone.CodeId
      );
      updates.ScheduledDeliveryDateFormatted = calcTimeZone(
        inputs.ScheduledDeliveryDate,
        e.target.value.TimeZone.CodeId
      );
    }
    if (e.target.name === "PickupTimeOut") {
      updates.PickupTimeOutFormatted = calcTimeZone(
        e.target.value,
        inputs.PickupLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "PickupTimeIn") {
      updates.PickupTimeInFormatted = calcTimeZone(
        e.target.value,
        inputs.PickupLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "DeliveryTimeIn") {
      updates.DeliveryTimeInFormatted = calcTimeZone(
        e.target.value,
        inputs.DeliveryLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "DeliveryTimeOut") {
      updates.DeliveryTimeOutFormatted = calcTimeZone(
        e.target.value,
        inputs.DeliveryLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "PickupTimeOutFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Pickup");
      updates.PickupTimeOut = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "PickupTimeInFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Pickup");
      updates.PickupTimeIn = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "DeliveryTimeInFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Delivery");
      updates.DeliveryTimeIn = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "DeliveryTimeOutFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Delivery");
      updates.DeliveryTimeOut = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "LoadTypeRecIdRef") {
      updates[e.target.name] = parseInt(value);
    }

    setInputs({ ...inputs, ...updates });
  };

  if (!data?.OrderArchive || SystemCodes?.loading || !States)
    return <p>Loading</p>;

  return (
    <div className="order-page">
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={3}>
            Order #{inputs?.OrderNumber}
          </Title>
          <Tag
            color={
              data?.OrderArchive?.Status?.CodeDescription
                ? getTagColorByOrderStatus(
                    data.OrderArchive.Status.CodeDescription
                  )
                : "danger"
            }
          >
            <b>
              {(
                data?.OrderArchive?.Status?.CodeDescription ?? "UNKNOWN"
              ).toUpperCase()}
            </b>
          </Tag>
          <div className="page-head-start--subheader">
            <IconLink
              icon="address-card"
              label={inputs?.Customer?.Name}
              to={`/finance/customers/${inputs?.CustomerRecIdRef}/details`}
            />
            <IconLink
              icon="route"
              label={inputs?.Route?.RouteId}
              to={`/dispatch/routes/${inputs?.RouteRecIdRef}/details`}
            />
          </div>
        </div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("summary")}
          as={Link}
          tabIndex="-1"
          to="summary"
        >
          <span>Summary</span>
        </Tab>
        <Tab
          active={location.pathname.includes("BOL-ELD")}
          as={Link}
          tabIndex="-1"
          to="BOL-ELD"
        >
          <span>BOL/ELD</span>
        </Tab>
        {isAuthorized && (
          <Tab
            active={location.pathname.includes("billing")}
            as={Link}
            tabIndex="-1"
            to="billing"
          >
            <span>Billing</span>
          </Tab>
        )}
      </Tab.Group>
      <Switch>
        <Route path="/dispatch/order-archive/:id/summary">
          <Summary
            editing={false}
            handleChangeCustomer={handleChangeCustomer}
            handleChangeRoute={handleChangeRoute}
            order={inputs}
            orderDetails={orderDetails}
            setOrderDetails={setOrderDetails}
            onChange={handleChange}
            onTimeFramesChange={times => setInputs({ ...inputs, ...times })}
          />
        </Route>
        <Route path="/dispatch/order-archive/:id/billing">
          {BillingAuth(Billing, {
            order: inputs,
            onChange: handleChange,
            editing: false
          })}
        </Route>
        <Route path="/dispatch/order-archive/:id/BOL-ELD">
          <BolEld editing={false} order={inputs} onChange={handleChange} />
        </Route>
      </Switch>
    </div>
  );
};

OrderArchivePage.propTypes = {};

export default OrderArchivePage;
